/**
 * RouteFallback
 * ---------------------------------------------------------------
 * Suspense fallback used while a lazy route chunk is loading.
 *
 * The previous fallback wiped the entire viewport (including the
 * top Navigation) and showed a centered spinner. Visually that
 * looked like a full page reload every time the user clicked
 * About / Blog / Contact.
 *
 * This fallback keeps the viewport stable: a slim top bar
 * (matching the real Navigation height) plus a soft skeleton
 * body. The chunk usually arrives in <300ms, so the user
 * perceives an instant page swap instead of a reload.
 */
const RouteFallback = () => {
  return (
    <div className="min-h-screen-dvh bg-background">
      {/* Fixed top bar placeholder (same height as Navigation) */}
      <div
        className="fixed top-0 left-0 right-0 z-40 h-16 bg-card/40 backdrop-blur-2xl border-b glass-border"
        aria-hidden="true"
      />

      <main className="pt-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-4xl mx-auto space-y-6 animate-pulse">
            <div className="h-3 w-24 rounded bg-muted/50 mx-auto" />
            <div className="h-10 sm:h-12 w-3/4 rounded bg-muted/50 mx-auto" />
            <div className="h-1 w-16 rounded bg-primary/40 mx-auto" />
            <div className="h-4 w-2/3 rounded bg-muted/40 mx-auto" />
            <div className="h-4 w-1/2 rounded bg-muted/40 mx-auto" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12 max-w-6xl mx-auto">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="rounded-2xl border border-primary/10 bg-card/40 p-6 h-44 animate-pulse"
              >
                <div className="h-10 w-10 rounded-xl bg-muted/50 mb-4" />
                <div className="h-4 w-2/3 rounded bg-muted/50 mb-2" />
                <div className="h-3 w-full rounded bg-muted/40" />
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Screen-reader only loading announcement */}
      <span className="sr-only" role="status" aria-live="polite">
        Loading page…
      </span>
    </div>
  );
};

export default RouteFallback;
